import type {
  CmmdSavedDataView,
  CmmdSavedDataViewDefinition,
  CmmdSavedDataViewSurface,
} from "@cmmd-forge/contracts";

export type DataViewSurface = CmmdSavedDataViewSurface;
export type DataViewLayout = "list" | "board" | "timeline";

/** How the list groups rows. "none" = flat (sorted only) — the launcher default. */
export type DataViewGroupBy = "none" | "status" | "priority" | "campaign" | "owner";
export type ProjectTaskGroupBy = "status" | "milestone";
export type ProjectTaskSortColumn = "title" | "assignee" | "owner" | "due";
export type ProjectTaskSortDirection = "asc" | "desc";

export interface ProjectTaskSort {
  readonly columnId: ProjectTaskSortColumn;
  readonly direction: ProjectTaskSortDirection;
}

/**
 * Forge's normalized view state. Task views intentionally use the same shape so
 * the controller and UI primitives stay reusable; CMMD persists only the fields
 * declared by that surface's versioned definition.
 */
export interface DataViewState {
  readonly layout: DataViewLayout;
  readonly search: string;
  readonly statusFilter: string;
  readonly workspaceFilter: string;
  readonly campaignFilter: string;
  readonly ownerFilter: string;
  readonly groupBy: DataViewGroupBy;
  /** Task-only, URL-local until CMMD's saved task-view schema supports it. */
  readonly milestoneFilter: string;
  /** Task-only, URL-local until CMMD's saved task-view schema supports it. */
  readonly taskGroupBy: ProjectTaskGroupBy;
  /** Task-only list sort, shareable in the URL and retained as a local last-view preference. */
  readonly taskSort?: ProjectTaskSort | null;
}

export interface SavedDataView {
  readonly id: string;
  readonly organizationId: number;
  readonly surface: DataViewSurface;
  readonly contextKey: string;
  readonly projectId: number | null;
  readonly name: string;
  readonly definitionVersion: number;
  readonly state: DataViewState;
  readonly isDefault: boolean;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SavedDataViewDraft {
  readonly name: string;
  readonly state: DataViewState;
  readonly setDefault: boolean;
}

export const EMPTY_DATA_VIEW_STATE: DataViewState = {
  layout: "list",
  search: "",
  statusFilter: "",
  workspaceFilter: "",
  campaignFilter: "",
  ownerFilter: "",
  groupBy: "none",
  milestoneFilter: "",
  taskGroupBy: "status",
};

export const DEFAULT_PROJECT_TASK_VIEW_STATE: DataViewState = {
  ...EMPTY_DATA_VIEW_STATE,
  layout: "board",
  taskGroupBy: "milestone",
};

export function dataViewStatesEqual(left: DataViewState, right: DataViewState): boolean {
  return (
    left.layout === right.layout &&
    left.search === right.search &&
    left.statusFilter === right.statusFilter &&
    left.workspaceFilter === right.workspaceFilter &&
    left.campaignFilter === right.campaignFilter &&
    left.ownerFilter === right.ownerFilter &&
    left.groupBy === right.groupBy &&
    left.milestoneFilter === right.milestoneFilter &&
    left.taskGroupBy === right.taskGroupBy &&
    (left.taskSort?.columnId ?? null) === (right.taskSort?.columnId ?? null) &&
    (left.taskSort?.direction ?? null) === (right.taskSort?.direction ?? null)
  );
}

/**
 * Compare only the fields CMMD persists for a surface. Task saved views store
 * layout + search; status/assignee filters stay URL/session-local so they must
 * not mark a saved view dirty.
 */
export function persistedDataViewStatesEqual(
  surface: DataViewSurface,
  left: DataViewState,
  right: DataViewState,
): boolean {
  if (surface === "cmmd.project.tasks") {
    return left.layout === right.layout && left.search === right.search;
  }
  return dataViewStatesEqual(left, right);
}

export function toCmmdSavedDataViewDefinition(
  surface: DataViewSurface,
  state: DataViewState,
): CmmdSavedDataViewDefinition {
  if (surface === "cmmd.project.tasks") {
    return {
      version: 1,
      surface,
      layout: state.layout,
      search: state.search,
    };
  }
  return {
    version: 1,
    surface,
    layout: state.layout,
    search: state.search,
    statusFilter: state.statusFilter,
    workspaceFilter: state.workspaceFilter,
    campaignFilter: state.campaignFilter,
    ownerFilter: state.ownerFilter,
    groupBy: state.groupBy,
  };
}

function normalizeDefinition(definition: CmmdSavedDataViewDefinition): DataViewState {
  if (definition.surface === "cmmd.project.tasks") {
    return {
      ...EMPTY_DATA_VIEW_STATE,
      layout: definition.layout,
      search: definition.search,
    };
  }
  return {
    layout: definition.layout,
    search: definition.search,
    statusFilter: definition.statusFilter,
    workspaceFilter: definition.workspaceFilter,
    campaignFilter: definition.campaignFilter,
    ownerFilter: definition.ownerFilter,
    groupBy: definition.groupBy,
    milestoneFilter: "",
    taskGroupBy: "status",
  };
}

export function fromCmmdSavedDataView(view: CmmdSavedDataView): SavedDataView {
  return {
    id: view.id,
    organizationId: view.organizationId,
    surface: view.surface,
    contextKey: view.contextKey,
    projectId: view.projectId,
    name: view.name,
    definitionVersion: view.definitionVersion,
    state: normalizeDefinition(view.definition),
    isDefault: view.isDefault,
    revision: view.revision,
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
  };
}
